import React, {Component, ReactElement, ReactNode} from 'react'

import NatAdminUserRepo, {SearchCondition} from './repository/NatAdminUserRepo'
import {AdminUserInfo, NatGetAdminUserList, SearchResult} from './repository/NatGetAdminUserList'
import {NatGetCondominiumGroup} from '../condominiumGroup/repository/NatGetCondominiumGroup'
import NatCondominiumGroupRepo from '../condominiumGroup/repository/NatCondominiumGroupRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea'
import PPTextField from '../../../components/shared/forms/PPTextField'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import {SortOrder} from '../../../components/shared/SortOrder'
import {SearchResultCountDisplayProps} from '../../../styledComponents/adminPage/SearchResultCountDisplay'
import {Messages} from '../../../Messages'
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import {SortableTableHeaderCell} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import {AVAILABLE_OR_NOT, ROLE_FOR_ADMINISTRATOR} from '../../../CodeMaster'
import PPCheckboxGroup from '../../../components/shared/forms/PPCheckboxGroup'
import {HKConstants} from '../../../HKConstants'
import {COMMON_CONSTANTS} from '../../../CommonConstants'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import NatCondominiumGroupSelectComponent from '../common/NatCondominiumGroupSelectComponent'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import styled from 'styled-components'
import {Box, Link, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material'
import {BoldSpan} from '../../../styledComponents/StyledSpan'
import {LeftRowFlexContainer, LeftRowFlexWrapContainer} from '../../../styledComponents/FlexComponents'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import {Location} from 'react-router-dom'

const KEY_SEARCH_CONDITION: string = 'adminAdminUserListPageSearchCondition'
const KEY_LOGIN_USER_CONDOMINIUM_GROUP_ID: string = 'loginUserCondominiumGroupId'

interface Props {
    AdminUserRepo: NatAdminUserRepo
    CondominiumGroupRepo: NatCondominiumGroupRepo
    eventEmitter: EventEmitterExAdmin
    location: Location

}

interface States {
    condominiumGroup: NatGetCondominiumGroup
    adminUsers: AdminUserInfo[]
    searchCondition: SearchCondition
    searchConditionSortInfo: SortOrder[]
    searchResult: SearchResult
    dispResultMessage: boolean
    loginUserCondominiumGroupId: number | null
    isLoadCompleted: boolean
}

class NatAdminAdminUserListPage extends Component<Props & AdminPagePropsSet, States> {
    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        const loginUserCondominiumGroupId: Nullable<number> =
            SessionStorageHelper.getNumberItemOrNull(KEY_LOGIN_USER_CONDOMINIUM_GROUP_ID)

        // 検索条件の初期設定
        let defaultSearchCondition: SearchCondition = {}
        if (loginUserCondominiumGroupId) {
            defaultSearchCondition.condominiumGroupId = loginUserCondominiumGroupId
        }
        defaultSearchCondition.roleId = ROLE_FOR_ADMINISTRATOR.values().map((master) => master.value).join(',')
        defaultSearchCondition.loginAvailability = AVAILABLE_OR_NOT.values().map((master) => master.value).join(',')

        // 前回検索条件が保持されていればデフォルト値として設定する
        const sessionStorageSearchCondition: string | null =
            SessionStorageHelper.getStringItemOrNull(KEY_SEARCH_CONDITION)

        if (sessionStorageSearchCondition) {
            defaultSearchCondition = JSON.parse(sessionStorageSearchCondition)
        }

        this.state = {

            condominiumGroup: {
                ['details']: null,
                ['informations']: [],
                ['requestError']: null,
                ['requestItemErrors']: [],
            },
            adminUsers: [],
            searchCondition: defaultSearchCondition,
            searchConditionSortInfo: [
                new SortOrder('publishStartDatetime', 'desc'),
                new SortOrder('status', 'asc'),
            ],
            searchResult: {},
            dispResultMessage: false,
            loginUserCondominiumGroupId: loginUserCondominiumGroupId,
            isLoadCompleted: false,
        }

        this.onChangeSelectedCondominiumGroupId = this.onChangeSelectedCondominiumGroupId.bind(this)
        this.changeLoginAvailability = this.changeLoginAvailability.bind(this)
        this.clickRole = this.clickRole.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.changeCondominiumCd = this.changeCondominiumCd.bind(this)
        this.changeCondominiumName = this.changeCondominiumName.bind(this)
        this.changeLoginId = this.changeLoginId.bind(this)
        this.changeFamilyName = this.changeFamilyName.bind(this)
        this.changeFirstName = this.changeFirstName.bind(this)
        this.changeFamilyNameKana = this.changeFamilyNameKana.bind(this)
        this.changeFirstNameKana = this.changeFirstNameKana.bind(this)
        this.registerAdminUser = this.registerAdminUser.bind(this)
    }

    async componentDidMount(): Promise<void> {

        await this.props.eventEmitter.showLoading()
        try {
            // 物件グループマスタ取得
            const condominiumGroupResponse: Nullable<NatGetCondominiumGroup> = await this.props.asyncCallFunction(
                this.props.CondominiumGroupRepo.getCondominiumGroup.bind(this.props.CondominiumGroupRepo),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (condominiumGroupResponse && condominiumGroupResponse.details) {
                this.setState({condominiumGroup: condominiumGroupResponse})
            }
            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    // 登録ボタン押下
    registerAdminUser(): void {
        this.props.navigate(
            '/admin/adminUserRegister',
        )
    }

    async onClickSearchButton(): Promise<void> {
        this.props.eventEmitter.clearHeadingAreaMessage()
        await this.searchAdminUsers(
            this.state.searchCondition,
            this.state.searchConditionSortInfo)
        // 検索条件を保持
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(this.state.searchCondition))
    }


    async searchAdminUsers(
        searchCondition?: SearchCondition,
        searchConditionSortInfo?: SortOrder[],
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            // 管理者ユーザ一覧取得
            const getAdminUsersResponse: Nullable<NatGetAdminUserList> =
                await this.props.asyncCallFunction(
                    this.props.AdminUserRepo.getAdminUserList.bind(
                        this.props.AdminUserRepo,
                        searchCondition,
                        searchConditionSortInfo,
                    ),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

            if (getAdminUsersResponse && getAdminUsersResponse.details) {
                this.setState({
                    adminUsers: getAdminUsersResponse.details.adminUserInfo,
                    searchResult: getAdminUsersResponse.details.searchResult,
                })
            }

        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({dispResultMessage: true})
        }
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.dispResultMessage) {
            if (this.state.searchResult.limitOverFlag === 1) {
                return {
                    noResultMessage:
                        Messages.IOPCC0016.toString(
                            HKConstants.VMN.ADM.ADMINISTRATOR_USER_SEARCH_RESULTS_LIMIT,
                            HKConstants.VMN.ADM.ADMINISTRATOR_USER_SEARCH_RESULTS_LIMIT,
                        ),
                }
            }
            if (this.state.adminUsers.length === 0) {
                return {noResultMessage: Messages.IOPCC0014.toString('')}
            }
        }

        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[2],
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
    }


    onChangeSelectedCondominiumGroupId(condomoniumGroupId: number): void {
        const searchCondition = this.state.searchCondition
        searchCondition.condominiumGroupId = condomoniumGroupId
        this.setState({searchCondition: searchCondition})
    }

    changeCondominiumCd(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.condominiumCd = event.target.value as string
        this.setState({searchCondition: searchCondition})
    }

    renderAdminUserRecord(adminUser: AdminUserInfo): Nullable<ReactElement> {
        const adminUserDetailUrl: string = `/admin/adminUser/${adminUser.adminId}`

        const roomToResidents: string[] = (adminUser.roomToResident) ?
            adminUser.roomToResident.map((userInfo) => {
                return (userInfo.ridgeName + ' ' + userInfo.roomNumber)
            }) : []

        return (
            <TableRow key={adminUser.adminId}>
                <TableCell align={'center'}><Link href={adminUserDetailUrl} underline="hover">{adminUser.loginId}</Link></TableCell>
                <TableCell align={'center'}>{adminUser.familyName} {adminUser.firstName}</TableCell>
                <TableCell align={'center'}>{adminUser.roleName}</TableCell>
                <TableCell align={'center'}>{adminUser.condominiumCd}</TableCell>
                <TableCellWordBreakAll align={'center'}>
                    {(adminUser.condominiumName) ?
                        adminUser.condominiumName : adminUser.condominiumGroupName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {(roomToResidents.length > 0) ?
                        roomToResidents.join(',') : ''}
                </TableCellWordBreakAll>
                <TableCell align={'center'}>{adminUser.status}</TableCell>
                <TableCell align={'center'}>{formatDateTimeForUpdateDate(adminUser.updateDatetime)}</TableCell>
            </TableRow>
        )
    }

    changeCondominiumName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.condominiumName = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    changeLoginId(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.loginId = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    changeFamilyName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.familyName = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    changeFirstName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.firstName = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    changeFamilyNameKana(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.familyNameKana = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    changeFirstNameKana(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition = this.state.searchCondition
        searchCondition.firstNameKana = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    clickRole(label: string, checked: boolean): void {
        const checkedRole = ROLE_FOR_ADMINISTRATOR.values().filter(role => {
            return role.label === label
        })

        if (this.state.searchCondition.roleId) {
            const checkedRoleArray: string[] = this.state.searchCondition.roleId.split(',')
            if (checked) {
                checkedRoleArray.push(checkedRole[0].value)
            } else {
                checkedRoleArray.splice(checkedRoleArray.indexOf(checkedRole[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.roleId = checkedRoleArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.roleId = checkedRole[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }

    changeLoginAvailability(label: string): void {
        const checkedValue: string[] = (this.state.searchCondition.loginAvailability) ?
            this.state.searchCondition.loginAvailability.split(',') : []

        const checkStatus = AVAILABLE_OR_NOT.values().filter(loginAvailability => {
            return loginAvailability.label === label
        })[0].value

        if (checkedValue.includes(checkStatus)) {
            checkedValue.splice(checkedValue.indexOf(checkStatus), 1)
        } else {
            checkedValue.push(checkStatus)
        }
        const searchCondition = this.state.searchCondition
        searchCondition.loginAvailability = checkedValue.join(',')
        this.setState({searchCondition: searchCondition})
    }

    judgeCheckeRoles(roleId: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.roleId) {
            return false
        }
        const roleArray: string[] = this.state.searchCondition.roleId.split(',')
        if (roleArray.indexOf(roleId) >= 0) {
            return true
        } else {
            return false
        }
    }

    judgeCheckeLoginAvailable(loginAvailability: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.loginAvailability) {
            return false
        }
        const loginAvailabilityArray: string[] = this.state.searchCondition.loginAvailability.split(',')
        if (loginAvailabilityArray.indexOf(loginAvailability) >= 0) {
            return true
        } else {
            return false
        }
    }

    renderRoleCheckBox(): ReactNode {
        return (
            <AuthorityLeftRowFlexContainer>
                {ROLE_FOR_ADMINISTRATOR.values().map((role) => {
                    return (
                        <PPCheckboxGroup
                            key={role.value}
                            data={[{
                                label: role.label,
                                checked: this.judgeCheckeRoles(role.value),
                            }]}
                            onChangeChecked={this.clickRole}
                        />
                    )
                })}
            </AuthorityLeftRowFlexContainer>
        )
    }

    renderAdminUserTable(adminUsers: AdminUserInfo[]): Nullable<ReactElement> {
        if (adminUsers.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = []
        headers.push(
            {id: 'adminId', label: '管理者ID'},
            {id: 'name', label: '氏名'},
            {id: 'role', label: '権限'},
            {id: 'condominiumCd', label: '物件コード'},
            {id: 'condominiumOrGroup', label: '物件グループ/物件名'},
            {id: 'roomNumber', label: '部屋番号'},
            {id: 'status', label: 'ステータス'},
            {id: 'updateDatetime', label: '更新日時'},
        )
        return (
            <>
                <Box mt={'10px'} ml={'10px'} pt={'16px'} pb={'16px'}>
                    <IllegalMarginedDivForTablePagination>
                        <BoldSpan>{this.state.searchResult.total}</BoldSpan>件
                        （1〜{this.state.searchResult.total}件目を表示）
                    </IllegalMarginedDivForTablePagination>
                </Box>
                <StyledHr/>
                <Table stickyHeader>
                    <TableHead>
                        <StyledTableRow>
                            {headers.map((header) => {
                                return (
                                    <StyledTableCell key={header.id} align={'center'}>{header.label}</StyledTableCell>
                                )
                            })}
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {this.state.adminUsers.map(
                            adminUser => this.renderAdminUserRecord(adminUser),
                        )}
                    </TableBody>
                </Table>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>

                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton}
                >
                    <ConditionTable>
                        <ConditionTableBody>
                            <ConditionTableRow>
                                <ConditionCategoryCell rowSpan={3}>物件情報</ConditionCategoryCell>
                                <ConditionColumNameCell>物件グループを選択</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    {
                                        this.state.condominiumGroup &&
                                        this.state.condominiumGroup.details &&
                                        <>
                                            <NatCondominiumGroupSelectComponent
                                                condominiumGroup={this.state.condominiumGroup.details}
                                                onChangeSelectedCondominiumGroupId=
                                                    {this.onChangeSelectedCondominiumGroupId}
                                                defaultValue={(this.state.searchCondition.condominiumGroupId) ?
                                                    this.state.searchCondition.condominiumGroupId : null}
                                                requiredFlag={true}
                                                eventKeys={['condominiumGroupId']}
                                                eventEmitter={this.props.eventEmitter}
                                                autoFocus={true}
                                            >
                                            </NatCondominiumGroupSelectComponent>
                                        </>
                                    }
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>物件コード</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 6,
                                            },
                                        }}
                                        required
                                        fullWidth
                                        onChange={this.changeCondominiumCd}
                                        defaultValue={this.state.searchCondition.condominiumCd}
                                        eventKeys={['condominiumCd']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>物件名</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 50,
                                            },
                                        }}
                                        required
                                        fullWidth
                                        onChange={this.changeCondominiumName}
                                        defaultValue={this.state.searchCondition.condominiumName}
                                        eventKeys={['condominiumName']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionCategoryCell rowSpan={5}>管理者ユーザ情報</ConditionCategoryCell>
                                <ConditionColumNameCell>権限</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <div style={{display: 'flex'}}>
                                        {this.renderRoleCheckBox()}
                                    </div>

                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>管理者ID</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 10,
                                            },
                                        }}
                                        required
                                        fullWidth
                                        onChange={this.changeLoginId}
                                        defaultValue={this.state.searchCondition.loginId}
                                        eventKeys={['adminId']}
                                        eventEmitter={this.props.eventEmitter}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>氏名</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <LeftRowFlexContainer>
                                        <FlexContainerColumn>
                                            <span>姓</span>
                                            <NameWrapper>
                                                <PPTextField
                                                    InputProps={{
                                                        inputProps: {
                                                            maxLength: 10,
                                                        },
                                                    }}
                                                    required
                                                    fullWidth
                                                    onChange={this.changeFamilyName}
                                                    defaultValue={this.state.searchCondition.familyName}
                                                    eventKeys={['familyName']}
                                                    eventEmitter={this.props.eventEmitter}
                                                />
                                            </NameWrapper>
                                        </FlexContainerColumn>
                                        <FlexContainerColumn>
                                            <span>名</span>
                                            <NameWrapper>
                                                <PPTextField
                                                    InputProps={{
                                                        inputProps: {
                                                            maxLength: 10,
                                                        },
                                                    }}
                                                    required
                                                    fullWidth
                                                    onChange={this.changeFirstName}
                                                    defaultValue={this.state.searchCondition.firstName}
                                                    eventKeys={['firstName']}
                                                    eventEmitter={this.props.eventEmitter}
                                                />
                                            </NameWrapper>
                                        </FlexContainerColumn>
                                    </LeftRowFlexContainer>
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>氏名(フリガナ)</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <LeftRowFlexContainer>
                                        <FlexContainerColumn>
                                            <span>セイ</span>
                                            <NameWrapper>
                                                <PPTextField
                                                    InputProps={{
                                                        inputProps: {
                                                            maxLength: 20,
                                                        },
                                                    }}
                                                    required
                                                    fullWidth
                                                    onChange={this.changeFamilyNameKana}
                                                    defaultValue={this.state.searchCondition.familyNameKana}
                                                    eventKeys={['familyNameKana']}
                                                    eventEmitter={this.props.eventEmitter}
                                                />
                                            </NameWrapper>
                                        </FlexContainerColumn>
                                        <FlexContainerColumn>
                                            <span>メイ</span>
                                            <NameWrapper>
                                                <PPTextField
                                                    InputProps={{
                                                        inputProps: {
                                                            maxLength: 20,
                                                        },
                                                    }}
                                                    required
                                                    fullWidth
                                                    onChange={this.changeFirstNameKana}
                                                    defaultValue={this.state.searchCondition.firstNameKana}
                                                    eventKeys={['firstNameKana']}
                                                    eventEmitter={this.props.eventEmitter}
                                                />
                                            </NameWrapper>
                                        </FlexContainerColumn>
                                    </LeftRowFlexContainer>
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>ログイン可否</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPCheckboxGroup
                                        row
                                        data={
                                            AVAILABLE_OR_NOT.values().map((available) => ({
                                                label: available.label,
                                                checked: this.judgeCheckeLoginAvailable(available.value),
                                            }))
                                        }
                                        onChangeChecked={this.changeLoginAvailability}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell>
                                    <AdminDefaultMediumButton
                                        onClick={this.onClickSearchButton}>
                                        管理者ユーザを検索する
                                    </AdminDefaultMediumButton>
                                </ConditionButtonCell>
                            </ConditionTableRow>
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                <SearchPageActionArea>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_ADMIN_USER}>
                        <AdminDefaultMediumButton onClick={this.registerAdminUser}>
                            新規
                        </AdminDefaultMediumButton>
                    </AuthorityChecker>
                </SearchPageActionArea>
                <PageMainArea>
                    {this.state.searchResult.limitOverFlag === 0 &&
                        this.renderAdminUserTable(this.state.adminUsers)
                    }
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminAdminUserListPage)

const StyledHr = styled.hr`
  border: none;
  height: 1px;
  margin: 0;
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.12);
`

const StyledTableRow = styled(TableRow)`
  white-space: nowrap;
`

const StyledTableCell = styled(TableCell)`
  &.MuiTableCell-root {
    background-color: #ffffff;
  }
`

const IllegalMarginedDivForTablePagination = styled.span`
  left: 0;
  margin-top: -8px;
`

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`

const AuthorityLeftRowFlexContainer = styled(LeftRowFlexWrapContainer)`
  flex: 1 1 0%;
`


const FlexContainerColumn: StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  &:first-child {
    padding: 0 12px 0 0;
  }
`

const NameWrapper: StyledDiv = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-end;

`
